import { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { adminAPI } from "@/services/api";
import { ClipboardList, DollarSign, MapPin, RefreshCw, TrendingUp, Users } from "lucide-react";
import {
  TailAdminDataTable,
  TailAdminEmptyState,
  TailAdminMetricCard,
  TailAdminPageHeader,
  TailAdminPanel,
  TailAdminStatusBadge,
} from "@/components/template/tailadmin/DashboardPrimitives";

const formatCurrency = (amount) =>
  new Intl.NumberFormat("en-NG", {
    style: "currency",
    currency: "NGN",
    minimumFractionDigits: 0,
  }).format(amount || 0);

const AdminBRMs = () => {
  const [brms, setBrms] = useState([]);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const fetchBrms = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminAPI.getBrms(statusFilter !== "all" ? statusFilter : null);
      setBrms(res.data?.items || []);
      setTotal(res.data?.total || 0);
    } catch {
      toast.error("Failed to load BRMs");
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchBrms();
  }, [fetchBrms]);

  const summary = useMemo(() => ({
    active: brms.filter((brm) => brm.status === "active").length,
    enrollments: brms.reduce((sum, brm) => sum + Number(brm.enrollment_count || 0), 0),
    activeEnrollments: brms.reduce((sum, brm) => sum + Number(brm.active_enrollment_count || 0), 0),
    earnings: brms.reduce((sum, brm) => sum + Number(brm.total_earnings || 0), 0),
  }), [brms]);

  const rows = brms.map((brm) => ({
    id: brm.id,
    cells: [
      <div>
        <p className="font-semibold text-gray-900">{brm.name || "Unnamed BRM"}</p>
        <p className="mt-1 inline-flex items-center gap-1 text-xs text-gray-500">
          <MapPin className="h-3.5 w-3.5" />
          {brm.region || "No region"}
        </p>
      </div>,
      <TailAdminStatusBadge tone={brm.status === "active" ? "success" : "neutral"}>{brm.status || "unknown"}</TailAdminStatusBadge>,
      <span className="font-medium text-gray-700">{Number(brm.enrollment_count || 0).toLocaleString()}</span>,
      <span className="font-medium text-[#039855]">{Number(brm.active_enrollment_count || 0).toLocaleString()}</span>,
      <span className="font-semibold text-gray-900">{formatCurrency(brm.total_earnings)}</span>,
    ],
  }));

  return (
    <div className="space-y-6">
      <TailAdminPageHeader
        eyebrow="Platform operators"
        title="BRMs"
        description="Monitor BRM status, regional activity, enrollments, and earnings."
        actions={
          <>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="h-11 w-44">
                <SelectValue placeholder="Filter" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All BRMs</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={fetchBrms} disabled={loading}>
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
          </>
        }
      />

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <TailAdminMetricCard label="Total BRMs" value={total.toLocaleString()} icon={Users} tone="primary" helper="Registered agents" loading={loading} />
        <TailAdminMetricCard label="Active BRMs" value={summary.active.toLocaleString()} icon={TrendingUp} tone="success" helper="Currently active" loading={loading} />
        <TailAdminMetricCard label="Enrollments" value={summary.enrollments.toLocaleString()} icon={ClipboardList} tone="warning" helper="Generated records" loading={loading} />
        <TailAdminMetricCard label="Earnings" value={formatCurrency(summary.earnings)} icon={DollarSign} tone="neutral" helper="Total commissions" loading={loading} />
      </div>

      <TailAdminPanel title="BRM directory" description={`${brms.length} BRMs loaded for current filter.`}>
        {loading || rows.length > 0 ? (
          <TailAdminDataTable
            loading={loading}
            columns={["BRM", "Status", "Enrollments", "Active", "Earnings"]}
            rows={rows}
          />
        ) : (
          <TailAdminEmptyState icon={Users} title="No BRMs found" description="No BRMs match the selected status filter." />
        )}
      </TailAdminPanel>
    </div>
  );
};

export default AdminBRMs;
